import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import cookies from 'js-cookie';
import UserService from '../services/UserService';
import './styles/HeroSlider.css';
import Datas from '../data/hero/hero-slider.json';

const languages = [
  {
    code: 'en',
    name: 'English',
    country_code: 'gb',
  }
];

function HeroSlider() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [prevSlide, setPrevSlide] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [transitionType, setTransitionType] = useState('fade'); // 'fade', 'slide', 'zoom'
  const intervalRef = useRef(null);
  const slidesRef = useRef([]);
  
  useEffect(() => {
    UserService.generateToken();
  }, []);

  const currentLanguageCode = cookies.get('i18next') || 'en';
  const currentLanguage = languages.find((l) => l.code === currentLanguageCode);
  const { t } = useTranslation();

  useEffect(() => {
     document.body.dir = currentLanguage?.dir ?? "ltr";;
    document.title = t('app_title');
  }, [currentLanguage, t]);

  // Preload images on component mount
  useEffect(() => {
    Datas.forEach((data, index) => {
      const img = new Image();
      img.src = process.env.PUBLIC_URL + `/assets/images/${data.backgroundImage}`;
      img.onload = () => {
        // Image loaded successfully
        slidesRef.current[index] = { ...data, imageLoaded: true };
      };
    });
  }, []);

  // Start autoplay on component mount
  useEffect(() => {
    startAutoPlay();
    return () => clearInterval(intervalRef.current);
  }, [currentSlide, isAnimating]);

  const startAutoPlay = () => {
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      if (!isAnimating) {
        handleSlideChange((currentSlide + 1) % Datas.length);
      }
    }, 5500); // 4 seconds between transitions
  };

  const handleSlideChange = (index) => {
    if (isAnimating || index === currentSlide) return;
    
    setIsAnimating(true);
    setPrevSlide(currentSlide);
    
    // Choose a random transition type for variety
    const transitionTypes = ['fade']; 
    // Enable this for more transition types:
    // const transitionTypes = ['fade', 'slide-left', 'slide-right', 'zoom'];
    const newTransitionType = transitionTypes[Math.floor(Math.random() * transitionTypes.length)];
    setTransitionType(newTransitionType);
    
    // Set timeout for slide change
    setTimeout(() => {
      setCurrentSlide(index);
      
      // Reset animation flag after transition completes
      setTimeout(() => {
        setIsAnimating(false);
        setPrevSlide(null);
      }, 2000); // Duration of fade-in animation
    }, 2000); // Duration of fade-out animation
  };

  const nextSlide = () => {
    const newIndex = (currentSlide + 1) % Datas.length;
    handleSlideChange(newIndex);
  };

  const prevSlideChange = () => {
    const newIndex = (currentSlide - 1 + Datas.length) % Datas.length;
    handleSlideChange(newIndex);
  };

  // Generate appropriate transition classes
  const getSlideClasses = (index) => {
    let classes = 'slider-item';
    
    if (index === currentSlide) {
      classes += ' active';
      if (prevSlide !== null) {
        classes += ' fade-in';
        classes += ` transition-${transitionType}-in`;
      }
    } else if (index === prevSlide) {
      classes += ' prev-active fade-out';
      classes += ` transition-${transitionType}-out`;
    }
    
    return classes;
  };

  return (
    <div className="hero-slider-wrapper">
      <section className="hero-slider-area">
        <div className="custom-slider">
          <div className="slider-container">
            {Datas.map((data, index) => (
              <div 
                key={index}
                className={getSlideClasses(index)}
                style={{ 
                  // Add custom rendering styles if needed
                  backgroundPosition: 'center',
                  backgroundSize: 'cover',
                }}
              >
                <div className="image-container">
                  <img 
                    src={process.env.PUBLIC_URL + `/assets/images/${data.backgroundImage}`} 
                    className="slider-image"
                    alt={`Slide ${index + 1}`}
                    loading="eager" // Preload images for smoother transitions
                    style={{
                      // Custom image rendering options
                      objectFit: 'cover',
                      objectPosition: 'center',
                      // Optional filter effects:
                      // filter: 'brightness(0.9) contrast(1.1)',
                    }}
                  />
                </div>
                <div className="slider-overlay"></div>
                <div className="slider-table">
                  <div className="slider-tablecell">
                    <Container>
                      <Row>
                        <Col md="12">
                          <div className={data.uniqClass}>
                            <div className="slider-title">
                              <p>{t('welcome_to_meghSikshak')}</p>
                            </div>
                            <div className="slider-desc">
                              <h1>{t(data.desc)}</h1>
                            </div>
                            <div className="slider-btn">
                              <Link 
                                className="slider-btn1" 
                                to={process.env.PUBLIC_URL + `/${data.btnOneLink}`}
                              >
                                {t('our_courses')}
                              </Link>
                              <Link 
                                className="slider-btn2" 
                                to={process.env.PUBLIC_URL + `/${data.btnTwoLink}`}
                              >
                                {t('contact_us')}
                              </Link>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </Container>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Navigation buttons */}
          <div className="slider-button-prev" onClick={prevSlideChange}>
            <i className="flaticon-arrow-left-th"></i>
          </div>
          <div className="slider-button-next" onClick={nextSlide}>
            <i className="flaticon-arrow-right-th"></i>
          </div>
          
          {/* Pagination dots */}
          {/* <div className="slider-pagination">
            {Datas.map((_, index) => (
              <span 
                key={index} 
                className={`pagination-dot ${index === currentSlide ? 'active' : ''}`}
                onClick={() => handleSlideChange(index)}
              ></span>
            ))}
          </div> */}
        </div>
      </section>
    </div>
  );
}

export default HeroSlider;